<template>
  <div
    :class="$b({
      hasMedia: hasMedia && !inModal,
      media: inModal ? 'hidden' : block.mediaVisibility,
      border: block.borderVisibility,
      inModal,
      promoTheme
    })"
  >
    <div
      :class="$b('intro-and-form')"
    >
      <div
        v-if="block.title || block.intro"
        :class="$b('intro')"
      >
        <VisionHeading
          v-if="block.title"
          size="h2"
          tag="h3"
          :class="$b('title')"
        >
          {{ block.title }}
        </VisionHeading>

        <RichText
          v-if="block.intro && !hideForm"
          :class="$b('intro')"
          :content="block.intro"
          size="body"
        />

        <div
          v-if="isVerifyMode"
          :class="$b('verify')"
        >
          <p
            v-if="!submitted"
          >
            Verifying your email address...
          </p>
          
          <SlideDownTransition>
            <p
              v-if="error"
              :class="$b('error')"
            >
              {{ error }}
            </p>
          </SlideDownTransition>

          <SlideDownTransition>
            <div
              v-if="showSuccessMessage"
              :class="$b('success-message')"
            >
              <VisionIcon icon="checkmark" />
              <p>
                {{ block.successCopy }}
              </p>
            </div>
          </SlideDownTransition>
        </div>
      </div>

      <form
        v-if="!isVerifyMode"
        :class="$b('form')"
        @submit.prevent="submit"
      >
        <template v-if="!hideForm">
          <TextInput
            v-model="state.email"
            :class="$b('input')"
            type="email"
            placeholder="Enter email address"
            :mode="promoTheme ? 'promo' : 'white'"
            :disabled="loading || submitted"
            :has-error="v$.email.$errors.length > 0"
            grow
          />

          <SlideDownTransition>
            <p
              v-if="v$.email.$errors.length > 0"
              :class="$b('error')"
            >
              {{ v$.email.$errors[0].$message }}
            </p>
          </SlideDownTransition>

          <VisionCheckbox
            v-if="block.showMailingListSubscribeCheckbox"
            id="subscribeToMailingList"
            v-model="subscribeToMailingList"
            input-value="subscribeToMailingList"
            name="subscribeToMailingList"
          >
            {{ block.mailingListCopy }}
          </VisionCheckbox>

          <component
            :is="promoTheme ? SecondaryButton : PrimaryButton"
            :class="$b('submit')"
            size="regular"
            :disabled="isSubmitDisabled"
            type="submit"
            :is-loading="loading"
            :is-complete="!loading && submitted"
            :square="inModal ? true : false"
            :align="inModal ? 'left' : 'center'"
            @click="submit"
          >
            {{ block.ctaCopy }}
          </component>

          <DatoStructuredText
            :class="$b('terms')"
            :content="block.terms"
            copy-size="body-sm"
          />

          <SlideDownTransition>
            <p
              v-if="error"
              :class="$b('error')"
            >
              {{ error }}
            </p>
          </SlideDownTransition>
        </template>

        <component :is="block.successReplacesForm ? 'div' : 'SlideDownTransition'">
          <div
            v-if="showSuccessMessage"
            :class="$b('success-message', { replacesForm: block.successReplacesForm })"
          >
            <VisionIcon
              v-if="!block.successReplacesForm"
              icon="checkmark"
            />

            <DatoStructuredText
              :class="$b('success-copy')"
              :content="block.successMessage"
              :cta-shape="inModal ? 'rectangle' : 'rounded'"
              :cta-align="inModal ? 'left' : 'center'"
              :cta-base-copy-size="inModal ? 'h3' : 'h4'"
              cta-grow
              copy-size="body"
              @close="$emit('close') /** We can embed a CTA to close the modal */"
            />
          </div>
        </component>

        <SlideDownTransition>
          <div
            v-if="showVerifyMessage"
            :class="$b('verify-message')"
          >
            <VisionIcon icon="error" />
            <p>
              {{ block.verifyCopy }}
            </p>
          </div>
        </SlideDownTransition>
      </form>
    </div>

    <div
      v-if="block.media"
      :class="$b('img-wrapper')"
    >
      <DatoCmsImage
        :class="$b('img')"
        :data="block.media?.responsiveImage"
        :picture-class="$b('picture')[0]"
        :picture-style="{
          objectFit: 'cover'
        }"
      />
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, email, helpers } from '@vuelidate/validators'
import { Image as DatoCmsImage } from 'vue-datocms'

import PrimaryButton from '~/components/atoms/button/PrimaryButton.vue'
import SecondaryButton from '~/components/atoms/button/SecondaryButton.vue'

export default defineNuxtComponent({
  name: 'DataCaptureBlock',
  
  components: {
    DatoCmsImage,
  },

  props: {
    block: {
      type: Object,
      required: true
    },
    inModal: {
      type: Boolean,
      default: false
    },
    promoTheme: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    loading: false,
    subscribeToMailingList: true,
    submitted: false,
    error: null
  }),

  setup () {
    const route = useRouter().currentRoute.value
    const userStore = useUserStore()

    const { secret, email: queryEmail } = route.query

    const state = reactive({
      email: queryEmail || null
    })

    const rules = {
      email: {
        required: helpers.withMessage('This field is required', required),
        email: helpers.withMessage('Please enter a valid email address', email)
      }
    }

    const v$ = useVuelidate(rules, state)
    return {
      state,
      v$,
      userStore,
      secret,
      PrimaryButton,
      SecondaryButton
    }
  },

  computed: {
    hasMedia () {
      return !!this.block.media
    },
    showSuccessMessage () {
      return this.submitted && !this.block.config.requireEmailVerification
    },
    hideForm () {
      return this.showSuccessMessage && this.block.successReplacesForm
    },
    showVerifyMessage () {
      return this.submitted && this.block.config.requireEmailVerification
    },
    isSubmitDisabled () {
      if (this.v$.email.$dirty && this.v$.email.$invalid) return true
      if (this.loading || this.submitted) return true
      if (
        this.block.showMailingListSubscribeCheckbox
        && this.block.requireMailingListSubscription
        && !this.subscribeToMailingList
      ) return true
      
      return false
    },
    isVerifyMode () {
      return this.secret
    }
  },

  async mounted () {
    if (this.isVerifyMode) {
      await this.verify()
    }
  },

  methods: {
    async submit () {
      this.error = null

      const isValid = await this.v$.$validate()
      if (!isValid) return

      this.userStore.setUserEmail(this.state.email)
      this.$track.updateUserData()

      this.loading = true

      let success = false
      try {
        await $fetch('/capture/submit', {
          method: 'POST',
          body: {
            email: this.state.email,
            subscribe: this.subscribeToMailingList,
            formId: this.block.config.id,
            url: window.location.href,
            slug: this.block.config.slug
          }
        })
        success = true
      } catch (e) {
        this.$sentry.captureException(e)
        this.error = 'Something went wrong, please try again.'
        this.loading = false
      }
      
      if (success) {
        setTimeout(() => {
          this.submitted = true
          this.loading = false
        }, 300)

        this.$track.form.submitted({
          formId: `data-capture-${this.block.config.slug}`,
          email: this.state.email,
          subscribe: this.subscribeToMailingList
        })
      }
    },
    async verify () {
      this.error = null
      this.loading = true

      let success = false
      try {
        const response = await $fetch('/capture/verify', {
          method: 'POST',
          body: {
            secret: this.secret,
            email: this.state.email
          }
        })
        success = true
        console.log(response)
      } catch (e) {
        this.$sentry.captureException(e)
        this.error = 'Something went wrong, please try again.'
      }

      if (success) {
        setTimeout(() => {
          this.submitted = true
          this.loading = false
        }, 300)
      }
    }
  }
})
</script>

<style lang="postcss">
.DataCaptureBlock {
  @apply w-full
    flex flex-col-reverse
    gap-sm md:gap-[var(--one-column)]
    overflow-visible
    items-center justify-center;

  --one-column: calc(1 / 12 * (100% - (11 * 32px)) + 32px);

  &--border-desktop,
  &--border-everywhere {
    @screen md {
      @apply border border-grey-500 py-12 px-10 xl:py-16 xl:px-20 rounded-lg;
    }
  }

  &--border-mobile,
  &--border-everywhere {
    @screen mdMax {
      @apply border border-grey-500 rounded-[10px] py-10 px-8;
    }
  }

  &--has-media:not(&--in-modal) {
    @screen md {
      @apply flex-row;
    }
  }

  &--in-modal {
    @apply overflow-visible;
  }
}

.DataCaptureBlock__intro-and-form {
  @apply flex flex-col gap-md w-full;

  .DataCaptureBlock--in-modal & {
    @apply gap-xs;
  }

  .DataCaptureBlock--has-media.DataCaptureBlock--media-desktop &,
  .DataCaptureBlock--has-media.DataCaptureBlock--media-everywhere & {
    @screen md {
      max-width: calc(50% - var(--one-column) / 2);
    }
  }

  .DataCaptureBlock--media-hidden:not(.DataCaptureBlock--in-modal) &,
  .DataCaptureBlock--media-mobile &,
  .DataCaptureBlock:not(.DataCaptureBlock--has-media):not(.DataCaptureBlock--in-modal) & {
    @screen md {
      @apply flex-row justify-around;
      
      .DataCaptureBlock__form {
        @apply max-w-md;
      }
    }
  }
}

.DataCaptureBlock__intro {
  @apply flex flex-col gap-sm;
}

.DataCaptureBlock__form {
  @apply flex flex-col gap-xs items-start
    w-full
    flex-grow
    md:py-xxxs mdMax:px-xxxs;

  .DataCaptureBlock--in-modal & {
    @apply px-0;
  }
}

.DataCaptureBlock__input {
  @apply w-full;
}

.DataCaptureBlock__submit {
  @apply w-full;
}

.DataCaptureBlock__terms {
  @apply opacity-60;
}

.DataCaptureBlock__img-wrapper {
  .DataCaptureBlock--border-desktop &,
  .DataCaptureBlock--border-everywhere & {
    @screen md {
      @apply -my-lg;
      margin-right: calc(var(--one-column) * -1 - 32px);
      height: calc(100% + 2 * theme('spacing.lg'));
    }
  }

  .DataCaptureBlock--border-mobile &,
  .DataCaptureBlock--border-everywhere & {
    @screen mdMax {
      @apply -mt-sm;
      margin-left: calc(-1 * var(--one-column) - 32px);
      margin-right: calc(-1 * var(--one-column) - 32px);
      width: calc(100% + 2 * var(--one-column) + 2 * 32px);
    }
  }

  .DataCaptureBlock--border-hidden & {
    @apply rounded-[10px] overflow-hidden;
  }

  .DataCaptureBlock--media-hidden & {
    @apply hidden;
  }

  .DataCaptureBlock--media-mobile & {
    @apply md:hidden;
  }

  .DataCaptureBlock--media-desktop & {
    @apply mdMax:hidden;
  }
}

.DataCaptureBlock__img {
  @apply h-full;
}

.DataCaptureBlock__error {
  @apply font-bold -mt-xxs text-rose-600 text-sm;
}

.DataCaptureBlock__success-message,
.DataCaptureBlock__verify-message {
  @apply flex items-center space-x-xxs
    text-white
    py-xs px-sm;
  border-radius: 4px;

  &--replaces-form {
    @apply py-0 px-0;
  }
}

.DataCaptureBlock__success-message {
  @apply bg-green;

  &--replaces-form {
    @apply bg-transparent;
  }
}

.DataCaptureBlock__verify-message {
  @apply bg-amber-500;
}
</style>